import React, { useState, useEffect, useMemo, useCallback } from 'react'
import axios from 'axios'
import { showLoading, GlobalLoading } from 'react-global-loading';
import { API_URL } from '../../../../../Constant';
import { ToastContainer, toast } from 'react-toastify';
import messages from '../../../../../assets/Messages.json'
import { useNavigate } from 'react-router-dom';
import { generateYearRange } from '../../../../../Custom/Custom';
import asiTitle from '../../../../../asi.json';

const BlockCReport = () => {
  const [itemData, setItemData] = useState({});
  const [blockCData, setBlockCData] = useState([]);
  const navigate = useNavigate();

  // Memoized block info from localStorage
  const blockInfo = useMemo(() => {
    const balanceSheetData = localStorage.getItem('balanceSheetFormData');
    if (!balanceSheetData) return {};

    const parsedData = JSON.parse(balanceSheetData);
    return {
      dslNo: parsedData.dslNo || '',
      surveyYear: parsedData.year || '2022-23',
      nameUnit: parsedData.nameUnit || '',
      pslNo: parsedData.pslNo || ''
    };
  }, []);

  const storedLanguage = localStorage.getItem('language') || 'en';

  // Memoized API configuration
  const getAuthConfig = useCallback(() => {
    const token = localStorage.getItem('jwt') ||
      localStorage.getItem('token') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('userToken');

    if (!token) {
      console.error('No token found in localStorage');
      return null;
    }

    return {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    };
  }, []);

  // Memoized data transformation
  const transformBlockCData = useCallback((data) => {
    const initialData = {};
    data.forEach(item => {
      initialData[item.assetCode] = {
        dslNo: item.dslNo,
        surveyYear: item.surveyYear,
        assetCode: item.assetCode,
        dueToRevaluation: item.dueToRevaluation,
        actualAdditions: item.actualAdditions,
        deductionAdjDuringYear: item.deductionAdjDuringYear,
        closingAsOn3456: item.closingAsOn3456,
        uptoYearBeginning: item.uptoYearBeginning,
        providedDuringYear: item.providedDuringYear,
        adjForSoldDiscardedDuringYear: item.adjForSoldDiscardedDuringYear,
        uptoYearEnd: item.uptoYearEnd,
        openingAsOn38: item.openingAsOn38,
        openingAsOn: item.openingAsOn,
        closingAsOn711: item.closingAsOn711,
        statusFlag: item.statusFlag,
        // All classes set to white for report view
        closingAsOn3456Class: 'textBox_back_color_white',
        uptoYearEndClass: 'textBox_back_color_white',
        openingAsOn38Class: 'textBox_back_color_white',
        closingAsOn711Class: 'textBox_back_color_white',
        providedDuringYearClass: 'textBox_back_color_white',
        openingAsOnClass: 'textBox_back_color_white',
        dueToRevaluationClass: 'textBox_back_color_white',
        actualAdditionsClass: 'textBox_back_color_white',
        deductionAdjDuringYearClass: 'textBox_back_color_white',
        adjForSoldDiscardedDuringYearClass: 'textBox_back_color_white',
        uptoYearBeginningClass: 'textBox_back_color_white'
      };
    });
    return initialData;
  }, []);

  // Fetch data function
  const fetchBlockCData = useCallback(async () => {
    if (!blockInfo.dslNo || !blockInfo.surveyYear) {
      console.log('Missing DSL No or Survey Year');
      return;
    }

    const requestPayload = {
      dsl_no: blockInfo.dslNo,
      survey_year: blockInfo.surveyYear.split('-')[0]
    };

    showLoading(true);

    try {
      const config = getAuthConfig();
      if (!config) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        return;
      }

      const response = await axios.post(`${API_URL}api/asi/blockCForm`, requestPayload, config);
      const { data, status } = response.data;

      if (status && data?.blockCData?.length > 0) {
        setBlockCData(data.blockCData);
        const transformedData = transformBlockCData(data.blockCData);
        setItemData(transformedData);
        console.log('Block C data loaded successfully:', data.blockCData.length, 'items');
      } else {
        setBlockCData([]);
        toast.warning('No Block C data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      }
    } catch (error) {
      console.error('API Error:', error);

      if (error.response?.status === 401) {
        toast.error('Session expired. Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.warning('No data found for this DSL No. and Survey Year.', messages.COLOR_RED);
      } else {
        toast.error(`Error loading data: ${error.message}`, messages.COLOR_RED);
      }
      setBlockCData([]);
    } finally {
      showLoading(false);
    }
  }, [blockInfo.dslNo, blockInfo.surveyYear, getAuthConfig, transformBlockCData]);

  useEffect(() => {
    fetchBlockCData();
  }, [fetchBlockCData]);

  // Memoized table headers
  const tableHeaders = useMemo(() => {
    const isHindi = storedLanguage === 'hi';
    return {
      slNo: isHindi ? asiTitle.hi.SL_NO : asiTitle.en.SL_NO,
      typeOfAssets: isHindi ? asiTitle.hi.TYPE_OF_ASSETS : asiTitle.en.TYPE_OF_ASSETS,
      grossValue: isHindi ? asiTitle.hi.GROSS_VALUE : asiTitle.en.GROSS_VALUE,
      description: isHindi ? asiTitle.hi.DESCRIPTION : asiTitle.en.DESCRIPTION,
      netValue: isHindi ? asiTitle.hi.NET_VALUE : asiTitle.en.NET_VALUE,
      openingAsOn: isHindi ? asiTitle.hi.OPENING_AS_ON : asiTitle.en.OPENING_AS_ON,
      additionalDuringYear: isHindi ? asiTitle.hi.ADDITIONAL_DURING_THE_YEAR : asiTitle.en.ADDITIONAL_DURING_THE_YEAR,
      deductionDuringYear: isHindi ? asiTitle.hi.DEDUCTION_DURING_YEAR : asiTitle.en.DEDUCTION_DURING_YEAR,
      closingAsOn: isHindi ? asiTitle.hi.CLOSING_AS_ON : asiTitle.en.CLOSING_AS_ON,
      uptoYearBeginning: isHindi ? asiTitle.hi.UP_TO_YEAR_BEGINING : asiTitle.en.UP_TO_YEAR_BEGINING,
      provideDuringYear: isHindi ? asiTitle.hi.PROVIDE_DURING_YEAR : asiTitle.en.PROVIDE_DURING_YEAR,
      soldDiscardDuringYear: isHindi ? asiTitle.hi.SOLD_DISCARD_DURING_YEAR : asiTitle.en.SOLD_DISCARD_DURING_YEAR,
      uptoYearEnd: isHindi ? asiTitle.hi.UP_TO_YEAR_END : asiTitle.en.UP_TO_YEAR_END,
      openingAsOns: isHindi ? asiTitle.hi.OPENING_AS_ONS : asiTitle.en.OPENING_AS_ONS,
      closingAsOns: isHindi ? asiTitle.hi.CLOSING_AS_ONS : asiTitle.en.CLOSING_AS_ONS,
      dueToRevaluation: isHindi ? asiTitle.hi.DUE_TO_REVALUATION : asiTitle.en.DUE_TO_REVALUATION,
      actualAddition: isHindi ? asiTitle.hi.ACTUAL_ADDITION : asiTitle.en.ACTUAL_ADDITION,
      dslNo: isHindi ? asiTitle.hi.DSL_NO : asiTitle.en.DSL_NO,
      surveyYear: isHindi ? asiTitle.hi.SERVEY_YEAR : asiTitle.en.SERVEY_YEAR,
      blockCTitle: isHindi ? asiTitle.hi.BLOCK_C_TITLE : asiTitle.en.BLOCK_C_TITLE
    };
  }, [storedLanguage]);

  // Memoized input field component
  const InputField = useCallback(({
    fieldName,
    index,
    item,
    className = ''
  }) => {
    const value = itemData[item.assetCode]?.[fieldName];
    const displayValue = value === "0" ? "" : value || '';

    return (
      <input
        type="text"
        className={`form-control ${className}`}
        maxLength="18"
        id={`${fieldName}${index}`}
        name={`${fieldName}${index}`}
        value={displayValue}
        readOnly
      />
    );
  }, [itemData]);

  // Memoized table row component
  const TableRow = useCallback(({ item, index }) => {
    const isBold = index === 7 || index === 9;

    return (
      <tr key={index}>
        <td>{index + 1}</td>
        <td className={isBold ? "bold-text" : ""}>{item.ItemName}</td>
        <td className={itemData[item.assetCode]?.openingAsOnClass || ''}>
          <InputField fieldName="openingAsOn" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.dueToRevaluationClass || ''}>
          <InputField fieldName="dueToRevaluation" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.actualAdditionsClass || ''}>
          <InputField fieldName="actualAdditions" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.deductionAdjDuringYearClass || ''}>
          <InputField fieldName="deductionAdjDuringYear" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.closingAsOn3456Class || ''}>
          <InputField fieldName="closingAsOn3456" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.uptoYearBeginningClass || ''}>
          <InputField fieldName="uptoYearBeginning" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.providedDuringYearClass || ''}>
          <InputField fieldName="providedDuringYear" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.adjForSoldDiscardedDuringYearClass || ''}>
          <InputField fieldName="adjForSoldDiscardedDuringYear" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.uptoYearEndClass || ''}>
          <InputField fieldName="uptoYearEnd" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.openingAsOn38Class || ''}>
          <InputField fieldName="openingAsOn38" index={index} item={item} />
        </td>
        <td className={itemData[item.assetCode]?.closingAsOn711Class || ''}>
          <InputField fieldName="closingAsOn711" index={index} item={item} />
        </td>
      </tr>
    );
  }, [itemData, InputField]);

  const handleCancel = useCallback(() => {
    navigate('/report-selection');
  }, [navigate]);

  return (
    <>
      <GlobalLoading />
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />

      <style>
        {`
          .report-container {
            background-color: #fff;
            border: 1px solid #ddd;
            border-radius: 6px;
            padding: 20px;
            margin-bottom: 20px;
          }
          .report-title {
            background-color: #fff;
            color: #333;
            font-size: 18px;
            font-weight: bold;
            padding: 15px 20px;
            margin: -20px -20px 20px -20px;
            border-radius: 6px 6px 0 0;
            border-bottom: 2px solid #333;
            text-align: center;
          }
          .report-info {
            background-color: #f8f9fa;
            padding: 10px 15px;
            margin-bottom: 20px;
            border-radius: 4px;
            border-left: 4px solid #333;
          }
          .report-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
            font-size: 12px;
          }
          .report-table th,
          .report-table td {
            border: 1px solid #333;
            padding: 6px 4px;
            text-align: center;
            vertical-align: middle;
          }
          .report-table th {
            background-color: #f5f5f5;
            color: #333;
            font-weight: bold;
            font-size: 11px;
          }
          .report-table td {
            background-color: #fff;
          }
          .report-table .asset-name {
            text-align: left;
            font-weight: 500;
            padding-left: 8px;
          }
          .report-table .bold-text {
            font-weight: bold;
          }
          .no-data {
            text-align: center;
            color: #666;
            font-style: italic;
            padding: 40px;
            background-color: #f8f9fa;
            border-radius: 4px;
          }
       
         
          @media print {
            .no-print { display: none !important; }
            .report-container { border: none; margin: 0; padding: 0; }
            .report-title { margin: 0; border-radius: 0; }
            .report-info { margin: 0 0 20px 0; }
            .btn_orange { display: none !important; }
          }
        `}
      </style>

      <div className="container-fluid py-3">
        {/* Report Content */}
        <div className="report-container">
          <div className="report-title">
            <div dangerouslySetInnerHTML={{ __html: tableHeaders.blockCTitle }} />
          </div>

          <div className="report-info">
            <div className="row">
              <div className="col-md-6">
                <strong>{tableHeaders.dslNo}:</strong> {blockInfo.dslNo || 'Not Available'}
              </div>
              <div className="col-md-6">
                <strong>{tableHeaders.surveyYear}:</strong> {blockInfo.surveyYear || 'Not Available'}
              </div>
            </div>
          </div>

          {blockCData.length > 0 ? (
            <div className="table-responsive">
              <table className="report-table">
                <thead>
                  <tr>
                    <th className="text-center" rowSpan="3" style={{ width: '5%' }}>{tableHeaders.slNo}</th>
                    <th className="text-center" rowSpan="3" style={{ width: '15%' }}>{tableHeaders.typeOfAssets}</th>
                    <th className="text-center" colSpan="5" style={{ width: '40%' }}>{tableHeaders.grossValue}</th>
                    <th className="text-center" colSpan="4" style={{ width: '25%' }}>{tableHeaders.description}</th>
                    <th className="text-center" colSpan="2" style={{ width: '15%' }}>{tableHeaders.netValue}</th>
                  </tr>
                  <tr>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>{tableHeaders.openingAsOn}</th>
                    <th className="text-center" colSpan="2" style={{ width: '16%' }}>{tableHeaders.additionalDuringYear}</th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>
                      <div dangerouslySetInnerHTML={{ __html: tableHeaders.deductionDuringYear }} />
                    </th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>
                      <div dangerouslySetInnerHTML={{ __html: tableHeaders.closingAsOn }} />
                    </th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>{tableHeaders.uptoYearBeginning}</th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>
                      <div dangerouslySetInnerHTML={{ __html: tableHeaders.provideDuringYear }} />
                    </th>
                    <th className="text-center" rowSpan="2" style={{ width: '9%' }}>
                      <div dangerouslySetInnerHTML={{ __html: tableHeaders.soldDiscardDuringYear }} />
                    </th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>
                      <div dangerouslySetInnerHTML={{ __html: tableHeaders.uptoYearEnd }} />
                    </th>
                    <th className="text-center" rowSpan="2" style={{ width: '8%' }}>
                      <div dangerouslySetInnerHTML={{ __html: tableHeaders.openingAsOns }} />
                    </th>
                    <th className="text-center" rowSpan="2" style={{ width: '7%' }}>
                      <div dangerouslySetInnerHTML={{ __html: tableHeaders.closingAsOns }} />
                    </th>
                  </tr>
                  <tr>
                    <th className="text-center" style={{ width: '8%' }}>{tableHeaders.dueToRevaluation}</th>
                    <th className="text-center" style={{ width: '8%' }}>{tableHeaders.actualAddition}</th>
                  </tr>
                  <tr>
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13].map(num => (
                      <th key={num} className="text-center">{num}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {blockCData.map((item, index) => {
                    const isBold = index === 7 || index === 9;
                    return (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        <td className={`asset-name ${isBold ? "bold-text" : ""}`}>{item.ItemName}</td>
                        <td>{itemData[item.assetCode]?.openingAsOn === "0" ? "" : itemData[item.assetCode]?.openingAsOn || ''}</td>
                        <td>{itemData[item.assetCode]?.dueToRevaluation === "0" ? "" : itemData[item.assetCode]?.dueToRevaluation || ''}</td>
                        <td>{itemData[item.assetCode]?.actualAdditions === "0" ? "" : itemData[item.assetCode]?.actualAdditions || ''}</td>
                        <td>{itemData[item.assetCode]?.deductionAdjDuringYear === "0" ? "" : itemData[item.assetCode]?.deductionAdjDuringYear || ''}</td>
                        <td>{itemData[item.assetCode]?.closingAsOn3456 === "0" ? "" : itemData[item.assetCode]?.closingAsOn3456 || ''}</td>
                        <td>{itemData[item.assetCode]?.uptoYearBeginning === "0" ? "" : itemData[item.assetCode]?.uptoYearBeginning || ''}</td>
                        <td>{itemData[item.assetCode]?.providedDuringYear === "0" ? "" : itemData[item.assetCode]?.providedDuringYear || ''}</td>
                        <td>{itemData[item.assetCode]?.adjForSoldDiscardedDuringYear === "0" ? "" : itemData[item.assetCode]?.adjForSoldDiscardedDuringYear || ''}</td>
                        <td>{itemData[item.assetCode]?.uptoYearEnd === "0" ? "" : itemData[item.assetCode]?.uptoYearEnd || ''}</td>
                        <td>{itemData[item.assetCode]?.openingAsOn38 === "0" ? "" : itemData[item.assetCode]?.openingAsOn38 || ''}</td>
                        <td>{itemData[item.assetCode]?.closingAsOn711 === "0" ? "" : itemData[item.assetCode]?.closingAsOn711 || ''}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">No Block C data available for this DSL No.</div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="row no-print">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => window.print()}
            >
              Print  
              <i className="fa fa-print ms-1"></i>
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/report-selection')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate(-1)}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button 
              type="button" 
              className="btn btn_orange mx-1"
              onClick={() => navigate('/blockDreport')}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>

      </div>
    </>
  )
}

export default BlockCReport